import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { Customer } from '@/lib/data-store';

interface CustomerFormProps {
  customer?: Customer;
  kodPelanggan: string;
  onSubmit: (data: Customer) => void;
  onCancel: () => void;
}

export const CustomerForm = ({ customer, kodPelanggan, onSubmit, onCancel }: CustomerFormProps) => {
  const [formData, setFormData] = useState<Partial<Customer>>(
    customer || {
      id: Date.now().toString(),
      kodPelanggan,
      namaSyarikat: '',
      email: '',
      telp: '',
      alamat: '',
      status: 'Aktif',
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.namaSyarikat?.trim()) newErrors.namaSyarikat = 'Nama Syarikat is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (!formData.telp?.trim()) newErrors.telp = 'Telepon is required';
    if (!formData.alamat?.trim()) newErrors.alamat = 'Alamat is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData as Customer);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Pelanggan Baru</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Kod Pelanggan (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Kod Pelanggan (Auto)</label>
              <Input
                type="text"
                value={formData.kodPelanggan || ''}
                disabled
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Kod akan di-jana secara automatik</p>
            </div>

            {/* Nama Syarikat */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Syarikat</label>
              <Input
                type="text"
                name="namaSyarikat"
                value={formData.namaSyarikat || ''}
                onChange={handleChange}
                placeholder="Nama Syarikat"
                className={errors.namaSyarikat ? 'border-red-500' : ''}
              />
              {errors.namaSyarikat && (
                <p className="text-sm text-red-600">{errors.namaSyarikat}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Emel</label>
              <Input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="emel@syarikat.com"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Telepon */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Telefon</label>
              <Input
                type="tel"
                name="telp"
                value={formData.telp || ''}
                onChange={handleChange}
                placeholder="03-1234-5678"
                className={errors.telp ? 'border-red-500' : ''}
              />
              {errors.telp && (
                <p className="text-sm text-red-600">{errors.telp}</p>
              )}
            </div>

            {/* Alamat */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Alamat</label>
              <textarea
                name="alamat"
                value={formData.alamat || ''}
                onChange={handleChange}
                placeholder="Alamat lengkap"
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.alamat ? 'border-red-500' : 'border-slate-300'
                }`}
              />
              {errors.alamat && (
                <p className="text-sm text-red-600">{errors.alamat}</p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={formData.status || 'Aktif'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Aktif">Aktif</option>
                <option value="Nonaktif">Tidak Aktif</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Tutup
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};